/* eslint-disable radix */

import { AxiosRequestConfig } from "axios";
import axios from "../misc/axios.config";
import FileSaver from "file-saver";
import { loginRequest, msalInstance } from "../misc/msal.config";

export default class ServiceBase {
  expiresIn: number | null;
  headers = {
    Authorization: "",
    "Ocp-Apim-Subscription-Key": process.env.REACT_APP_SUBSCRIPTION_KEY,
    "Accept-Language": "en",
    "Content-Type": "application/json",
  };
  config: AxiosRequestConfig = {
    headers: this.headers,
    withCredentials: true,
  };

  constructor() {
    this.expiresIn = null;
  }

  setAuthenticationHeaders = async () => {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }

    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    });

    this.headers = { ...this.headers, Authorization: `${response.tokenType} ${response.accessToken}` };
  };

  setDefaultHeaders = async () => {
    await this.setAuthenticationHeaders();

    this.config = { ...this.config, headers: { ...this.headers, "Content-Type": "application/json" } };
  };

  setFormDataHeaders = async () => {
    this.setAuthenticationHeaders();

    this.config = { ...this.config, headers: { ...this.headers, "Content-Type": "multipart/form-data" } };
  };

  delete = async <P>(url: string) => {
    await this.setDefaultHeaders();
    const result = await axios.delete<P>(url, this.config);
    return result.data;
  };

  get = async <P>(url: string) => {
    await this.setDefaultHeaders();
    const result = await axios.get<P>(url, this.config);
    return result.data;
  };

  post = async <P>(url: string, data?: any) => {
    await this.setDefaultHeaders();
    const result = await axios.post<P>(url, data, this.config);
    return result.data;
  };

  postFile = async <P>(url: string, formData?: FormData) => {
    await this.setFormDataHeaders();
    const result = await axios.post<P>(url, formData, this.config);
    return result.data;
  };

  put = async <P>(url: string, data?: any) => {
    await this.setDefaultHeaders();
    const result = await axios.put<P>(url, data, this.config);
    return result.data;
  };

  putFile = async <P>(url: string, formData?: FormData) => {
    await this.setFormDataHeaders();
    const result = await axios.put<P>(url, formData, this.config);
    return result.data;
  };

  _download = async (url: string) => {
    await this.setDefaultHeaders();
    const config: AxiosRequestConfig = {
      ...this.config,
      responseType: "blob",
      timeout: 300000,
    };
    
    const response = await axios.get(url, config);
    const contentDisposition = response.headers["content-disposition"];

    if (contentDisposition !== undefined) {
      const match = contentDisposition.match("filename=(.+);");

      if (match != null) {
        const filename = (match[1] as string).replace(/"/g, "");
        FileSaver.saveAs(response.data, filename);
      }
    }
  };
}
